import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  filterNonDeletedAreas,
  findAreaNameConflict,
} from "../../hooks/AreaHook";

const DEFAULT_POSITION = 2;

/**
 * AddAreaModal
 * Pop-up used by Area Management to create an area.
 * Fields: Area Name (must be unique), Area Status, Area Position.
 */
const AddAreaModal = ({
  open,
  onClose,
  onSubmit,
  allAreas,
  loading,
  defaultStatus = "Active",
}) => {
  const initialStatus = defaultStatus === "Inactive" ? "Inactive" : "Active";
  const nonDeletedCount = filterNonDeletedAreas(allAreas).length;
  const maxPosition = nonDeletedCount + 1;

  const [name, setName] = useState("");
  const [status, setStatus] = useState(initialStatus);
  const [position, setPosition] = useState(String(DEFAULT_POSITION));
  const [touched, setTouched] = useState(false);

  // Reset the form every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setName("");
    setStatus(initialStatus);
    setPosition(String(Math.min(DEFAULT_POSITION, maxPosition)));
    setTouched(false);
  }, [open, initialStatus]);

  const trimmedName = name.trim();
  const conflict = useMemo(
    () => (trimmedName ? findAreaNameConflict(allAreas, trimmedName) : null),
    [allAreas, trimmedName]
  );

  let nameError = "";
  if (conflict) {
    nameError = `An area named "${conflict.name}" already exists${
      conflict.isDeleted === true ? " (deleted)" : ""
    }. Area names must be unique.`;
  } else if (touched && !trimmedName) {
    nameError = "Area Name is required.";
  }

  const positionNumber = Number(position);
  const positionValid =
    Number.isInteger(positionNumber) &&
    positionNumber >= 1 &&
    positionNumber <= maxPosition;
  const positionError = positionValid
    ? ""
    : `Enter a whole number from 1 to ${maxPosition}.`;

  const canSubmit = !!trimmedName && !conflict && positionValid && !loading;

  const handleSubmit = () => {
    setTouched(true);
    if (!canSubmit) return;
    onSubmit({
      name: trimmedName,
      isActive: status === "Active",
      position: positionNumber,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={titleStyles}>Add Area</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
          <TextField
            label="Area Name"
            value={name}
            autoFocus
            fullWidth
            size="small"
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            error={!!nameError}
            helperText={nameError || " "}
            sx={fieldStyles}
            autoComplete="off"
          />
          <TextField
            select
            label="Area Status"
            value={status}
            fullWidth
            size="small"
            onChange={(e) => setStatus(e.target.value)}
            sx={fieldStyles}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </TextField>
          <TextField
            label="Area Position"
            type="number"
            value={position}
            fullWidth
            size="small"
            inputProps={{ min: 1, max: maxPosition, step: 1 }}
            onChange={(e) => setPosition(e.target.value)}
            error={!positionValid}
            helperText={
              positionError ||
              "Areas at this position and after move down by one."
            }
            sx={fieldStyles}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "0px 24px 20px 24px" }}>
        <Button onClick={onClose} disabled={loading} sx={cancelButtonStyles}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          sx={submitButtonStyles}
        >
          {loading ? "Adding..." : "Add Area"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAreaModal;

const titleStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 18,
};

const fieldStyles = {
  "& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root": {
    fontFamily: "Avenir Book, sans-serif",
  },
};

const cancelButtonStyles = {
  textTransform: "none",
  color: "#808080",
  fontFamily: "Avenir Book, sans-serif",
};

const submitButtonStyles = {
  textTransform: "none",
  background: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};
